import * as THREE from 'three';
import { DROPS, RIVER_LENGTH, SPLIT_DROP_D, type River } from '../game/River';
import { createSeededRandom } from '../utils/random';

const SPACING = 2.2;
const PICKUP_RADIUS = 0.8;
/** Each crumb pickup is a little cluster of torn bread chunks. */
const CHUNKS = 3;

/**
 * Bread crumbs floating along the racing line. They show where the fast water
 * goes and reward holding it; gaps appear where a rock sits on the line.
 */
export class Crumbs {
  readonly mesh: THREE.InstancedMesh;
  readonly total: number;
  private readonly x: number[] = [];
  private readonly y: number[] = [];
  private readonly z: number[] = [];
  private readonly taken: boolean[] = [];
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly scale = new THREE.Vector3();
  private readonly pos = new THREE.Vector3();
  private readonly euler = new THREE.Euler();
  /** Per chunk: offset from the crumb centre, size and resting tilt. */
  private readonly chunkOffset: THREE.Vector3[] = [];
  private readonly chunkSize: number[] = [];
  private readonly chunkSpin: number[] = [];

  constructor(river: River) {
    for (let d = 8; d < RIVER_LENGTH - 2; d += SPACING) {
      if ([...DROPS.map(([dropD]) => dropD), SPLIT_DROP_D].some((dropD) => d > dropD - 0.6 && d < dropD + 2.5)) continue;
      const x = river.laneX(d);
      const z = -d;
      if (river.rocks.some((rock) => Math.hypot(rock.x - x, rock.z - z) < rock.r + 0.7)) continue;
      if (river.whirlpools.some((pool) => Math.hypot(pool.x - x, pool.z - z) < pool.radius)) continue;
      this.x.push(x);
      this.y.push(river.heightAt(d, x) + 0.05);
      this.z.push(z);
      this.taken.push(false);
    }
    this.total = this.x.length;

    // A lumpy, flat-shaded chunk reads as torn bread rather than a bead.
    const rng = createSeededRandom(99);
    const geometry = new THREE.IcosahedronGeometry(1, 0);
    const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
    const jitter = new Map<string, number>();
    for (let i = 0; i < pos.count; i += 1) {
      const key = `${pos.getX(i).toFixed(3)},${pos.getY(i).toFixed(3)},${pos.getZ(i).toFixed(3)}`;
      let k = jitter.get(key);
      if (k === undefined) {
        k = 0.7 + rng() * 0.5;
        jitter.set(key, k);
      }
      pos.setXYZ(i, pos.getX(i) * k, pos.getY(i) * k * 0.75, pos.getZ(i) * k);
    }
    geometry.computeVertexNormals();

    this.mesh = new THREE.InstancedMesh(
      geometry,
      new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 1, flatShading: true, emissive: '#4a2a08', emissiveIntensity: 0.3 }),
      this.total * CHUNKS,
    );
    const color = new THREE.Color();
    for (let i = 0; i < this.total * CHUNKS; i += 1) {
      const angle = (i % CHUNKS) * ((Math.PI * 2) / CHUNKS) + rng() * 0.8;
      const radius = i % CHUNKS === 0 ? 0 : 0.19 + rng() * 0.06;
      this.chunkOffset.push(new THREE.Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius));
      this.chunkSize.push(i % CHUNKS === 0 ? 0.18 : 0.11 + rng() * 0.04);
      this.chunkSpin.push(rng() * Math.PI * 2);
      // Toasted crumbs with darker crust bits: warm enough to stand out on white foam.
      this.mesh.setColorAt(i, color.set(rng() < 0.4 ? '#a8612a' : '#e2a857'));
    }
    this.mesh.castShadow = true;
    this.mesh.frustumCulled = false;
  }

  reset(): void {
    this.taken.fill(false);
  }

  /** Returns how many crumbs the duck picked up this frame. */
  update(time: number, duck: THREE.Vector3): number {
    let collected = 0;
    for (let i = 0; i < this.total; i += 1) {
      if (!this.taken[i]) {
        const dx = duck.x - this.x[i];
        const dz = duck.z - this.z[i];
        if (dx * dx + dz * dz < PICKUP_RADIUS * PICKUP_RADIUS && Math.abs(duck.y - this.y[i]) < 1.2) {
          this.taken[i] = true;
          collected += 1;
        }
      }
      // Floating on the surface: a gentle bob and rock, no spinning.
      const bob = Math.sin(time * 2.4 + i) * 0.02;
      for (let c = 0; c < CHUNKS; c += 1) {
        const k = i * CHUNKS + c;
        this.scale.setScalar(this.taken[i] ? 0 : this.chunkSize[k]);
        this.euler.set(Math.sin(time * 1.7 + k) * 0.25, this.chunkSpin[k], Math.cos(time * 1.3 + k) * 0.2);
        this.quat.setFromEuler(this.euler);
        const offset = this.chunkOffset[k];
        this.pos.set(this.x[i] + offset.x, this.y[i] + bob, this.z[i] + offset.z);
        this.matrix.compose(this.pos, this.quat, this.scale);
        this.mesh.setMatrixAt(k, this.matrix);
      }
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    return collected;
  }

  get collected(): number {
    return this.taken.filter(Boolean).length;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
